<template>
  <div class="p-3 pb-24 bg mb-auto h-full overflow-auto bg-gradient-to-b from-gray-800 text-white" id="style-1">
    <!-- Header -->
    <div class="bg-gray-900 bg-opacity-80 border border-gray-800 rounded-md mb-3 shadow-xl px-4 py-3 flex items-center justify-between">
      <div class="flex items-center gap-3">
        <i class="fas fa-trophy text-indigo-400 text-lg"></i>
        <h1 class="text-lg font-semibold text-indigo-300 leading-tight">อันดับผู้เล่น</h1>
      </div>
      <div class="flex items-center gap-2">
        <!-- ตัวกรองตึก -->
        <select
          v-model="selectedBuilding"
          class="px-3 py-1.5 bg-gray-700 text-white text-xs font-medium rounded-md border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          @change="fetchSurvivors"
        >
          <option :value="null">ทั้งหมด</option>
          <option value="1">ตึก 1</option>
          <option value="2">ตึก 2</option>
        </select>
        <button
          v-if="selectedBuilding !== null"
          @click="showWeeklyRewardModal = true"
          class="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 rounded-md text-xs font-medium border border-indigo-400 transition-colors"
        >
          <i class="fas fa-gift mr-1"></i> รางวัลประจำสัปดาห์ตึก {{ selectedBuilding }}
        </button>
      </div>
    </div>

    <!-- Current Player -->
    <section v-if="currentPlayerRank" class="mb-4">
      <div class="text-xs text-indigo-300 mb-1.5 font-semibold">
        <i class="fas fa-user-shield mr-1"></i>อันดับของคุณ
      </div>
      <article class="bg-gray-900 bg-opacity-70 rounded-md p-3 border-2 border-indigo-400 transition-all duration-150">
        <div class="flex items-center gap-3">
          <!-- Rank bubble -->
          <div class="w-9 h-9 flex items-center justify-center rounded-full text-white text-sm font-semibold border"
               :class="rankBubbleClass((currentPlayerRank || 1) - 1)">
            #{{ currentPlayerRank || '?' }}
          </div>
          <!-- Avatar -->
          <div class="relative" v-if="playerCard">
            <img :src="playerCard.imageUrl || fallbackImg" class="w-12 h-12 rounded-md object-cover border-2 border-indigo-400" @error="handleImageError" />
            <div class="absolute -bottom-1 -right-1 bg-indigo-600 text-white text-xxs font-semibold rounded-full w-5 h-5 flex items-center justify-center border border-gray-600">
              {{ playerCard.level || 'N/A' }}
            </div>
          </div>
          <!-- Info -->
          <div class="flex-1 min-w-0" v-if="playerCard">
            <div class="flex items-center gap-1">
              <h3 class="text-indigo-200 text-sm font-semibold truncate">{{ playerCard.characterName || 'ไม่ทราบ' }}</h3>
              <i class="fas fa-user-shield text-indigo-400 text-xs ml-1" title="นี่คือตัวคุณ"></i>
            </div>
            <div class="grid grid-cols-3 gap-1 text-xxs text-indigo-200 opacity-90">
              <span><i class="fas fa-building mr-1"></i>ตึก: {{ playerCard.building }}</span>
              <span class="col-span-2"><i class="fas fa-door-closed mr-1"></i>ห้อง: {{ playerCard.room }}</span>
              <span class="col-span-3"><i class="fas fa-clock mr-1"></i>{{ formatSurvivalTime(playerCard.survivalStartTime) }}</span>
            </div>
          </div>
          <div v-else class="flex-1 min-w-0">
            <div class="text-sm text-gray-400">กำลังโหลดข้อมูล...</div>
          </div>
          <!-- Admin eye -->
          <button
            v-if="isAdmin && playerCard"
            @click="openCharacterView(playerCard.docId)"
            class="text-indigo-200 hover:text-indigo-400 transition-colors ml-auto"
            title="ดูข้อมูลตัวละคร"
          >
            <i class="fas fa-eye"></i>
          </button>
        </div>
      </article>
    </section>

    <!-- Loading / Error -->
    <div v-if="isLoading && !survivors.length" class="space-y-2">
      <div v-for="n in 6" :key="n" class="bg-gray-900 bg-opacity-70 rounded-md p-3 border border-gray-800 animate-pulse">
        <div class="flex items-center gap-3">
          <div class="w-9 h-9 rounded-full bg-gray-700 border border-gray-600"></div>
          <div class="w-12 h-12 rounded-md bg-gray-700 border-2 border-gray-600"></div>
          <div class="flex-1">
            <div class="h-3 bg-gray-700 rounded mb-2 w-2/3"></div>
            <div class="h-2 bg-gray-700 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="error" class="text-center text-red-300 py-8 text-sm">
      {{ error }}
    </div>

    <!-- Survivors list -->
    <section v-else>
      <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        <article
          v-for="survivor in displaySurvivors"
          :key="survivor.docId"
          class="bg-gray-900 bg-opacity-70 rounded-md p-3 border transition-all duration-150"
          :class="cardClass(survivor.originalIndex, survivor)"
        >
          <div class="flex items-center gap-3">
            <!-- Rank bubble -->
            <div class="w-9 h-9 flex items-center justify-center rounded-full text-white text-sm font-semibold border"
                 :class="rankBubbleClass(survivor.originalIndex)">
              #{{ survivor.originalIndex + 1 }}
            </div>

            <!-- Avatar -->
            <div class="relative">
              <img
                :src="survivor.imageUrl || fallbackImg"
                alt="ผู้รอดชีวิต"
                class="w-12 h-12 rounded-md object-cover border-2 border-indigo-400"
                @error="handleImageError"
              />
              <div class="absolute -bottom-1 -right-1 bg-indigo-600 text-white text-xxs font-semibold rounded-full w-5 h-5 flex items-center justify-center border border-gray-600">
                {{ survivor.level || 'N/A' }}
              </div>
            </div>

            <!-- Info -->
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-1">
                <h3 class="text-indigo-200 text-sm font-semibold truncate">{{ survivor.characterName || 'ไม่ทราบ' }}</h3>
                <div v-if="isAdmin" class="text-blue-500 text-xs "><i class="fas fa-gem mr-1"></i>{{ survivor.diamond }}</div>
                <div v-if="isAdmin" class="text-yellow-500 text-xs "><i class="fas fa-g mr-1"></i>{{ survivor.gold  }}</div>
                <div v-if="isAdmin" class="text-silver-300 text-xs "><i class="fas fa-coins mr-1"></i>{{ survivor.silver  }}</div>
                <i v-if="survivor.docId === userId" class="fas fa-user-shield text-indigo-400 text-xs ml-1" title="นี่คือตัวคุณ"></i>
              </div>
              <div class="grid grid-cols-3 gap-1 text-xxs text-indigo-200 opacity-90">
                <span><i class="fas fa-building mr-1"></i>ตึก: {{ survivor.building }}</span>
                <span class="col-span-2"><i class="fas fa-door-closed mr-1"></i>ห้อง: {{ survivor.room }}</span>
                <span class="col-span-3"><i class="fas fa-clock mr-1"></i>{{ formatSurvivalTime(survivor.survivalStartTime) }}</span>
              </div>
            </div>

            <!-- Admin eye -->
            <button
              v-if="isAdmin"
              @click="openCharacterView(survivor.docId)"
              class="text-indigo-200 hover:text-indigo-400 transition-colors ml-auto"
              title="ดูข้อมูลตัวละคร"
            >
              <i class="fas fa-eye"></i>
            </button>
          </div>
        </article>
      </div>

      <!-- Empty -->
      <div v-if="survivors.length === 0" class="text-center text-indigo-200 py-10 text-sm">
        ไม่พบผู้รอดชีวิตที่มีมากกว่าหรือเท่ากับ 5 ห้อง
      </div>

      <!-- Load more -->
      <div v-if="(isAdmin || survivors.length < 75) && hasMoreData && !isLoadingMore" class="text-center mt-5">
        <button
          @click="loadMoreSurvivors"
          class="px-4 py-2 w-full bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700 border border-indigo-400 transition-colors"
        >
          โหลดเพิ่ม
        </button>
      </div>
      <div v-if="isLoadingMore" class="flex flex-col items-center py-5">
        <div class="animate-spin rounded-full h-8 w-8 border-t-4 border-indigo-600 mb-2"></div>
        <p class="text-indigo-200 text-xs">กำลังโหลดข้อมูลเพิ่ม...</p>
      </div>
      <div v-if="!hasMoreData && survivors.length > 0" class="text-center text-indigo-200 text-xs mt-4">
        ไม่มีข้อมูลเพิ่มเติม
      </div>
    </section>

    <WeeklyRewardsModal v-if="showWeeklyRewardModal" :building="selectedBuilding" @close="showWeeklyRewardModal = false" />
  </div>
</template>

<script>
import { ref, onMounted, computed, watch } from 'vue';
import { projectFirestore, projectAuth } from '@/firebase/config';
import { useRouter } from 'vue-router';
import { usePlayerStore } from '@/composables/usePlayerStore';
import dayjs from 'dayjs';
import 'dayjs/locale/th';
import relativeTime from 'dayjs/plugin/relativeTime';
import WeeklyRewardsModal from '@/components/WeeklyRewardsModal.vue';

dayjs.extend(relativeTime);
dayjs.locale('th');

export default {
  name: 'RankingPage',
  components: { WeeklyRewardsModal },
  setup() {
    const survivors = ref([]);
    const currentPlayer = ref(null);
    const playerCardData = ref(null);
    const rankPlayerArray = ref([]);
    const rankPlayerArrayBuildingOne = ref([]);
    const rankPlayerArrayBuildingTwo = ref([]);
    const isLoading = ref(true);
    const isLoadingMore = ref(false);
    const error = ref(null);
    const userId = ref(null);
    const isAdmin = ref(false);
    const router = useRouter();
    const lastDoc = ref(null);
    const hasMoreData = ref(true);
    const pageSize = 25;
    const showWeeklyRewardModal = ref(false);
    const selectedBuilding = ref(null);
    const playerStore = usePlayerStore();

    const fallbackImg = 'https://via.placeholder.com/64';
    const handleImageError = (e) => { e.target.src = fallbackImg; };

    const displaySurvivors = computed(() => {
      if (!userId.value) return survivors.value.map((s, idx) => ({ ...s, originalIndex: idx }));
      return survivors.value
        .map((s, idx) => ({ ...s, originalIndex: idx }))
        .filter(s => s.docId !== userId.value);
    });

    const currentPlayerRank = computed(() => {
      if (!userId.value) return null;

      const playerBuilding = playerCardData.value ? Number(playerCardData.value.building) || 1 : null;
      let rankings;

      if (selectedBuilding.value !== null && playerBuilding !== null && Number(selectedBuilding.value) !== playerBuilding) {
        return null;
      }

      if (selectedBuilding.value === null) {
        rankings = rankPlayerArray.value;
      } else if (Number(selectedBuilding.value) === 1) {
        rankings = rankPlayerArrayBuildingOne.value;
      } else if (Number(selectedBuilding.value) === 2) {
        rankings = rankPlayerArrayBuildingTwo.value;
      } else {
        rankings = [];
      }

      const index = rankings.findIndex(uid => uid === userId.value);
      return index !== -1 && (index + 1) <= 100 ? index + 1 : null;
    });

    const playerCard = computed(() => {
      return currentPlayer.value || playerCardData.value;
    });

    const fetchUserId = async () => {
      try {
        const user = projectAuth.currentUser;
        if (user) {
          userId.value = user.uid;
          playerStore.init(user.uid);
        } else {
          error.value = 'กรุณาล็อกอินเพื่อดูข้อมูล';
        }
      } catch (err) {
        error.value = 'เกิดข้อผิดพลาดในการดึงข้อมูลผู้ใช้: ' + err.message;
      }
    };

    watch(() => playerStore.userData, (data) => {
      if (data) {
        isAdmin.value = data.admin === true;
      }
    }, { immediate: true });

    watch(() => playerStore.characterData, (data) => {
      if (data && userId.value) {
        currentPlayer.value = { docId: userId.value, ...data };
      } else {
        currentPlayer.value = null;
      }
    }, { immediate: true });

    const fetchRankPlayer = async () => {
      try {
        const [rankDoc, rankBuildingOneDoc, rankBuildingTwoDoc] = await Promise.all([
          projectFirestore.collection('server').doc('rank').get(),
          projectFirestore.collection('server').doc('rankBuildingOne').get(),
          projectFirestore.collection('server').doc('rankBuildingTwo').get()
        ]);

        rankPlayerArray.value = rankDoc.exists ? (rankDoc.data().rankings || []) : [];
        rankPlayerArrayBuildingOne.value = rankBuildingOneDoc.exists ? (rankBuildingOneDoc.data().rankings || []) : [];
        rankPlayerArrayBuildingTwo.value = rankBuildingTwoDoc.exists ? (rankBuildingTwoDoc.data().rankings || []) : [];

        // ดึงข้อมูลตัวละครตรงๆ ไม่รอ playerStore
        if (userId.value) {
          await fetchPlayerCardData();
        }
      } catch (err) {
        console.warn('rank fetch error', err);
        error.value = 'เกิดข้อผิดพลาดในการดึงข้อมูลอันดับ: ' + err.message;
      }
    };

    const fetchPlayerCardData = async () => {
      if (!userId.value) return;
      try {
        const doc = await projectFirestore.collection('characters').doc(userId.value).get();
        if (doc.exists) {
          playerCardData.value = { docId: userId.value, ...doc.data() };
        }
      } catch (err) {
        console.warn('player card fetch error', err);
      }
    };

    const baseQuery = () => {
      let query = projectFirestore
        .collection('characters')
        .where('isLive', '==', true)
        .where('room', '>=', 5)
        .orderBy('room', 'desc')
        .orderBy('survivalStartTime', 'asc');
      
      if (selectedBuilding.value !== null) {
        query = query.where('building', '==', Number(selectedBuilding.value));
      }
      
      return query;
    };

    const fetchSurvivors = async () => {
      try {
        isLoading.value = true;
        survivors.value = [];
        const snapshot = await baseQuery().limit(pageSize).get();
        survivors.value = snapshot.docs.map(d => ({ docId: d.id, ...d.data() }));
        lastDoc.value = snapshot.docs.length ? snapshot.docs[snapshot.docs.length - 1] : null;
        hasMoreData.value = snapshot.docs.length === pageSize;
      } catch (err) {
        error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูล: ' + err.message;
      } finally {
        isLoading.value = false;
      }
    };

    const loadMoreSurvivors = async () => {
      if (!hasMoreData.value || isLoadingMore.value || !lastDoc.value) return;
      try {
        isLoadingMore.value = true;
        const snapshot = await baseQuery().startAfter(lastDoc.value).limit(pageSize).get();
        const more = snapshot.docs.map(d => ({ docId: d.id, ...d.data() }));
        survivors.value = [...survivors.value, ...more];
        lastDoc.value = snapshot.docs.length ? snapshot.docs[snapshot.docs.length - 1] : null;
        hasMoreData.value = snapshot.docs.length === pageSize;
      } catch (err) {
        error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลเพิ่ม: ' + err.message;
      } finally {
        isLoadingMore.value = false;
      }
    };

    const formatSurvivalTime = (timestamp) => {
      if (!timestamp || !timestamp.toDate) return 'ไม่ทราบ';
      try {
        const start = dayjs(timestamp.toDate());
        const now = dayjs();
        const diff = now.diff(start);
        const d = Math.floor(diff / (1000 * 60 * 60 * 24));
        const h = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        let out = 'รอดมา: ';
        if (d > 0) out += `${d} วัน `;
        if (h > 0 || d > 0) out += `${h} ชั่วโมง `;
        out += `${m} นาที`;
        return out.trim();
      } catch {
        return 'ไม่ทราบ';
      }
    };

    const openCharacterView = (docId) => {
      const routeUrl = router.resolve({ name: 'AdminCharacterView', params: { userId: docId } }).href;
      window.open(routeUrl, '_blank');
    };

    const rankBubbleClass = (index) => {
      if (index === 0) return 'bg-yellow-500 border-yellow-300';
      if (index === 1) return 'bg-gray-400 border-gray-200';
      if (index === 2) return 'bg-yellow-700 border-yellow-500';
      return 'bg-indigo-600 border-indigo-400';
    };

    const cardClass = (index, s) => {
      const cls = [];
      if (index === 0) cls.push('border-yellow-400');
      else if (index === 1) cls.push('border-gray-400');
      else if (index === 2) cls.push('border-yellow-600');
      else cls.push('border-gray-800');

      if (s && s.docId === userId.value) {
        cls.push('border-2', 'border-indigo-400', 'cursor-default');
      } else {
        cls.push('hover:border-indigo-400');
      }
      return cls;
    };

    onMounted(async () => {
      await fetchUserId();
      await fetchRankPlayer();
      await fetchSurvivors();
    });

    return {
      survivors,
      currentPlayer,
      currentPlayerRank,
      isLoading,
      isLoadingMore,
      error,
      userId,
      isAdmin,
      hasMoreData,
      displaySurvivors,
      playerCard,
      playerCardData,
      showWeeklyRewardModal,
      selectedBuilding,
      loadMoreSurvivors,
      formatSurvivalTime,
      handleImageError,
      openCharacterView,
      rankBubbleClass,
      cardClass,
      fetchSurvivors
    };
  }
};
</script>

<style scoped>
#style-1::-webkit-scrollbar { width: 6px; }
#style-1::-webkit-scrollbar-track { background: #1f2937; border-radius: 8px; }
#style-1::-webkit-scrollbar-thumb { background: #374151; border-radius: 8px; }
#style-1::-webkit-scrollbar-thumb:hover { background: #4b5563; }

.animate-spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

.text-xxs { font-size: 0.65rem; line-height: 0.85rem; }

.bg {
  background: url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2FGuestroom.png?alt=media&token=fc8f6e11-d047-4811-a36a-e7f225ef9d27') no-repeat center center fixed,
  linear-gradient(to bottom, rgba(31,41,55,0.8), rgba(0,0,0,0.9));
}
</style>